import React, { useState, useEffect } from 'react';
import { Settings, User, Save, Loader2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import AppHeader from '@/components/AppHeader';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

const SettingsPage = () => {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [profile, setProfile] = useState({
    name: '',
    email: '',
    company: ''
  });

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || '',
        email: user.email || '',
        company: user.company || ''
      });
    }
  }, [user]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/auth/profile`, profile);
      toast.success('Profile updated successfully');
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <AppHeader title="Settings" />
      
      <div className="p-6 md:p-8">
        <div className="max-w-6xl mx-auto">

        {/* Profile Settings */}
        <Card className="bg-white border-zinc-200 mb-6">
          <CardHeader>
            <CardTitle className="text-zinc-900 flex items-center gap-2">
              <User className="w-5 h-5 text-amber-500" />
              Profile Settings
            </CardTitle>
            <CardDescription className="text-zinc-500">Update your personal information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">Full Name</label>
              <Input
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                className="bg-zinc-50 border-zinc-200"
                data-testid="settings-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">Email</label>
              <Input
                value={profile.email}
                disabled
                className="bg-zinc-100 border-zinc-200 text-zinc-500"
                data-testid="settings-email-input"
              />
              <p className="text-zinc-400 text-xs mt-1">Email cannot be changed</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">Company</label>
              <Input
                value={profile.company}
                onChange={(e) => setProfile({ ...profile, company: e.target.value })}
                placeholder="Your company name"
                className="bg-zinc-50 border-zinc-200"
                data-testid="settings-company-input"
              />
            </div>
            <Button
              onClick={handleSaveProfile}
              disabled={saving}
              className="bg-amber-500 hover:bg-amber-600"
              data-testid="save-profile-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : saved ? (
                <Check className="w-4 h-4 mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Profile
            </Button>
          </CardContent>
        </Card>

        {/* Current Workspace Info */}
        <Card className="bg-white border-zinc-200">
          <CardHeader>
            <CardTitle className="text-zinc-900 flex items-center gap-2">
              <Settings className="w-5 h-5 text-amber-500" />
              Current Workspace
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-zinc-100">
                <span className="text-zinc-600">Workspace Name</span>
                <span className="text-zinc-900 font-medium">{currentWorkspace?.name || 'My Workspace'}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-zinc-100">
                <span className="text-zinc-600">Workspace ID</span>
                <span className="text-zinc-900 font-medium">{currentWorkspace?.display_id || 'WS-001'}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-zinc-600">Credits Balance</span>
                <span className="text-amber-500 font-medium">{currentWorkspace?.credits || 0} credits</span>
              </div>
            </div>
          </CardContent>
        </Card>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
